<script setup>
import { reactive, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import InboxesAPI from 'dashboard/api/inboxes';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import TemplateButtonsBuilder from './components/TemplateButtonsBuilder.vue';
import TemplatePreview from './components/TemplatePreview.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const getTemplatesFn = useMapGetter('inboxes/getWhatsAppTemplates');
const templates = computed(() => {
  const list = getTemplatesFn.value(props.inbox.id);
  return Array.isArray(list) ? list : [];
});

const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const NAME_REGEX = /^[a-z0-9_]+$/;

const WHATSAPP_LANGUAGES = [
  { value: 'pt_BR', label: 'Portuguese (BR)' },
  { value: 'pt_PT', label: 'Portuguese (PT)' },
  { value: 'en', label: 'English' },
  { value: 'en_US', label: 'English (US)' },
  { value: 'en_GB', label: 'English (UK)' },
  { value: 'es', label: 'Spanish' },
  { value: 'es_ES', label: 'Spanish (ES)' },
  { value: 'es_AR', label: 'Spanish (AR)' },
  { value: 'es_MX', label: 'Spanish (MX)' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
];

const categoryOptions = computed(() => [
  {
    value: 'MARKETING',
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.MARKETING'),
  },
  {
    value: 'UTILITY',
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.UTILITY'),
  },
  {
    value: 'AUTHENTICATION',
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.AUTHENTICATION'),
  },
]);

const headerFormatOptions = computed(() => [
  { value: 'NONE', label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.NONE') },
  { value: 'TEXT', label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.TEXT') },
  { value: 'IMAGE', label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.IMAGE') },
  { value: 'VIDEO', label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.VIDEO') },
  {
    value: 'DOCUMENT',
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.DOCUMENT'),
  },
]);

const form = reactive({
  category: 'MARKETING',
  name: '',
  language: 'pt_BR',
  headerFormat: 'NONE',
  headerText: '',
  headerExample: '',
  bodyText: '',
  footerText: '',
  buttons: [],
});

const bodyExamples = reactive({});
const mediaFile = ref(null);
const mediaPreviewUrl = ref('');
const isCreating = ref(false);
const isSyncing = ref(false);
const confirmingDeleteName = ref('');
const deletingName = ref('');
const expandedTemplateName = ref('');

const extractVariables = text => {
  const matches = text?.match(/{{\s*[\w]+\s*}}/g) || [];
  return [...new Set(matches.map(m => m.replace(/[{}\s]/g, '')))];
};

const bodyVariables = computed(() => extractVariables(form.bodyText));
const headerHasVariable = computed(
  () => extractVariables(form.headerText).length > 0
);
const isMediaHeader = computed(() => MEDIA_FORMATS.includes(form.headerFormat));

const mediaAccept = computed(() => {
  if (form.headerFormat === 'IMAGE') return 'image/*';
  if (form.headerFormat === 'VIDEO') return 'video/*';
  return '.pdf,application/pdf';
});

const previewHeader = computed(() => ({
  format: form.headerFormat,
  text: form.headerText,
}));

const nameError = computed(() => {
  if (!form.name) return null;
  return NAME_REGEX.test(form.name)
    ? null
    : t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_INVALID');
});

const validationError = computed(() => {
  if (!form.name || !NAME_REGEX.test(form.name)) {
    return t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_INVALID');
  }
  if (!form.bodyText.trim()) {
    return t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_REQUIRED');
  }
  if (isMediaHeader.value && !mediaFile.value) {
    return t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.MEDIA_REQUIRED');
  }
  const invalidButton = form.buttons.find(button => {
    if (!button.text?.trim()) return true;
    if (button.type === 'URL' && !button.url?.trim()) return true;
    if (button.type === 'PHONE_NUMBER' && !button.phoneNumber?.trim()) {
      return true;
    }
    return false;
  });
  if (invalidButton) {
    return t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_INCOMPLETE');
  }
  const missingExamples = bodyVariables.value.some(
    key => !bodyExamples[key]?.trim()
  );
  if (bodyVariables.value.length && missingExamples) {
    return t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES_REQUIRED');
  }
  return null;
});

const toggleExpand = name => {
  expandedTemplateName.value = expandedTemplateName.value === name ? '' : name;
};

const getComponent = (template, type) => {
  return template.components?.find(c => c.type === type);
};

const statusMeta = status => {
  const map = {
    APPROVED: { icon: 'i-lucide-circle-check', color: 'text-n-teal-11' },
    PENDING: { icon: 'i-lucide-clock', color: 'text-n-amber-11' },
    REJECTED: { icon: 'i-lucide-circle-x', color: 'text-n-ruby-10' },
  };
  return map[status?.toUpperCase()] || map.PENDING;
};

const onMediaChange = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  mediaFile.value = file;
  if (mediaPreviewUrl.value) URL.revokeObjectURL(mediaPreviewUrl.value);
  mediaPreviewUrl.value =
    form.headerFormat === 'IMAGE' ? URL.createObjectURL(file) : '';
};

const onHeaderFormatChange = value => {
  form.headerFormat = value;
  mediaFile.value = null;
  if (mediaPreviewUrl.value) {
    URL.revokeObjectURL(mediaPreviewUrl.value);
    mediaPreviewUrl.value = '';
  }
};

const buildPayload = () => {
  const payload = {
    name: form.name.trim(),
    language: form.language,
    category: form.category,
    body: { text: form.bodyText },
  };

  if (bodyVariables.value.length) {
    payload.body.examples = bodyVariables.value.map(
      key => bodyExamples[key]?.trim() || `sample_${key}`
    );
  }

  if (form.headerFormat === 'TEXT' && form.headerText.trim()) {
    payload.header = { format: 'TEXT', text: form.headerText };
    if (headerHasVariable.value && form.headerExample) {
      payload.header.example = [form.headerExample];
    }
  } else if (isMediaHeader.value) {
    payload.header = { format: form.headerFormat };
  }

  if (form.footerText.trim()) {
    payload.footer = { text: form.footerText };
  }

  if (form.buttons.length) {
    payload.buttons = form.buttons.map(button => {
      const mapped = { type: button.type, text: button.text };
      if (button.type === 'URL') {
        mapped.url = button.url;
        if (button.url.includes('{{') && button.example) {
          mapped.example = [button.example];
        }
      }
      if (button.type === 'PHONE_NUMBER') {
        mapped.phone_number = button.phoneNumber;
      }
      return mapped;
    });
  }

  return payload;
};

const resetForm = () => {
  form.category = 'MARKETING';
  form.name = '';
  form.language = 'pt_BR';
  form.headerFormat = 'NONE';
  form.headerText = '';
  form.headerExample = '';
  form.bodyText = '';
  form.footerText = '';
  form.buttons = [];
  Object.keys(bodyExamples).forEach(key => delete bodyExamples[key]);
  mediaFile.value = null;
  if (mediaPreviewUrl.value) {
    URL.revokeObjectURL(mediaPreviewUrl.value);
    mediaPreviewUrl.value = '';
  }
};

const createTemplate = async () => {
  if (validationError.value) {
    useAlert(validationError.value);
    return;
  }

  try {
    isCreating.value = true;
    const formData = new FormData();
    formData.append('template', JSON.stringify(buildPayload()));
    if (isMediaHeader.value && mediaFile.value) {
      formData.append('header_media', mediaFile.value);
    }
    await store.dispatch('inboxes/createMessageTemplate', {
      inboxId: props.inbox.id,
      formData,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CREATE_SUCCESS'));
    resetForm();
  } catch (error) {
    const message =
      error.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CREATE_ERROR');
    useAlert(message);
  } finally {
    isCreating.value = false;
  }
};

const syncTemplates = async () => {
  try {
    isSyncing.value = true;
    await store.dispatch('inboxes/syncTemplates', props.inbox.id);
    await store.dispatch('inboxes/get');
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

const deleteTemplate = async name => {
  try {
    deletingName.value = name;
    await store.dispatch('inboxes/deleteMessageTemplate', {
      inboxId: props.inbox.id,
      name,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.DELETE_SUCCESS'));
  } catch (error) {
    const message =
      error.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.DELETE_ERROR');
    useAlert(message);
  } finally {
    deletingName.value = '';
    confirmingDeleteName.value = '';
  }
};

// --- Template Library ---
const showLibrary = ref(false);
const libraryTemplates = ref([]);
const libraryError = ref('');
const isLoadingLibrary = ref(false);
const libraryCategory = ref('UTILITY');
const libraryLanguage = ref('');
const librarySearch = ref('');
const expandedLibraryName = ref('');

const libraryLanguageOptions = [
  { value: '', label: 'Todos os idiomas' },
  ...WHATSAPP_LANGUAGES,
];

const libraryCategoryOptions = [
  { value: '', label: 'Todos' },
  {
    value: 'UTILITY',
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.UTILITY'),
  },
  {
    value: 'MARKETING',
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.MARKETING'),
  },
  {
    value: 'AUTHENTICATION',
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.AUTHENTICATION'),
  },
];

const filteredLibraryTemplates = computed(() => {
  if (!librarySearch.value.trim()) return libraryTemplates.value;
  const q = librarySearch.value.toLowerCase();
  return libraryTemplates.value.filter(tpl => {
    const body = tpl.components?.find(c => c.type === 'BODY');
    return (
      tpl.name?.toLowerCase().includes(q) ||
      body?.text?.toLowerCase().includes(q)
    );
  });
});

const fetchLibrary = async () => {
  try {
    isLoadingLibrary.value = true;
    libraryTemplates.value = [];
    libraryError.value = '';
    const params = {};
    if (libraryCategory.value) params.category = libraryCategory.value;
    if (libraryLanguage.value) params.language = libraryLanguage.value;
    const { data } = await InboxesAPI.fetchTemplateLibrary(
      props.inbox.id,
      params
    );
    libraryTemplates.value = data.templates || [];
  } catch (error) {
    libraryError.value =
      error.response?.data?.error || 'Erro ao carregar biblioteca de modelos';
    useAlert(libraryError.value);
  } finally {
    isLoadingLibrary.value = false;
  }
};

const openLibrary = () => {
  showLibrary.value = true;
  if (!libraryTemplates.value.length) {
    fetchLibrary();
  }
};

const getLibraryComponent = (tpl, type) => {
  return tpl.components?.find(c => c.type === type);
};

const useLibraryTemplate = tpl => {
  const body = getLibraryComponent(tpl, 'BODY');
  const header = getLibraryComponent(tpl, 'HEADER');
  const footer = getLibraryComponent(tpl, 'FOOTER');
  const buttons = getLibraryComponent(tpl, 'BUTTONS');

  form.category = tpl.category || 'UTILITY';
  form.language = tpl.language || 'pt_BR';
  form.name = '';
  form.bodyText = body?.text || '';
  form.footerText = footer?.text || '';

  if (header?.format === 'TEXT') {
    form.headerFormat = 'TEXT';
    form.headerText = header.text || '';
  } else if (header?.format && header.format !== 'NONE') {
    form.headerFormat = header.format;
    form.headerText = '';
  } else {
    form.headerFormat = 'NONE';
    form.headerText = '';
  }
  form.headerExample = '';

  if (buttons?.buttons?.length) {
    form.buttons = buttons.buttons.map(btn => ({
      type: btn.type || 'QUICK_REPLY',
      text: btn.text || '',
      url: btn.url || '',
      phoneNumber: btn.phone_number || '',
      example: '',
    }));
  } else {
    form.buttons = [];
  }

  Object.keys(bodyExamples).forEach(key => delete bodyExamples[key]);
  mediaFile.value = null;
  if (mediaPreviewUrl.value) {
    URL.revokeObjectURL(mediaPreviewUrl.value);
    mediaPreviewUrl.value = '';
  }

  showLibrary.value = false;
  useAlert(
    'Modelo carregado no formulário. Defina um nome e ajuste o conteúdo.'
  );
};
</script>

<template>
  <div class="flex flex-col gap-8 mx-6 max-w-7xl">
    <!-- Existing templates -->
    <section class="flex flex-col gap-4">
      <div class="flex justify-between items-center">
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.SUBTITLE') }}
          </p>
        </div>
        <NextButton
          sm
          slate
          faded
          icon="i-lucide-refresh-cw"
          :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.SYNC')"
          :is-loading="isSyncing"
          @click="syncTemplates"
        />
      </div>

      <p
        v-if="!templates.length"
        class="py-6 text-sm text-center rounded-lg text-n-slate-11 bg-n-slate-2"
      >
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.EMPTY') }}
      </p>

      <div v-else class="flex flex-col gap-2">
        <div
          v-for="template in templates"
          :key="template.id || template.name"
          class="rounded-lg outline outline-1 outline-n-weak"
        >
          <div
            class="flex gap-3 justify-between items-center p-3 cursor-pointer hover:bg-n-slate-2"
            @click="toggleExpand(template.name)"
          >
            <div class="flex gap-2 items-center min-w-0">
              <Icon
                :icon="
                  expandedTemplateName === template.name
                    ? 'i-lucide-chevron-down'
                    : 'i-lucide-chevron-right'
                "
                class="flex-shrink-0 size-4 text-n-slate-10"
              />
              <div class="flex flex-col gap-1 min-w-0">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ template.name }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ template.category }} · {{ template.language }}
                </span>
              </div>
            </div>
            <div class="flex flex-shrink-0 gap-3 items-center">
              <span
                class="flex gap-1 items-center text-xs font-medium"
                :class="statusMeta(template.status).color"
              >
                <Icon
                  :icon="statusMeta(template.status).icon"
                  class="size-3.5"
                />
                {{ template.status }}
              </span>
              <template v-if="confirmingDeleteName === template.name">
                <NextButton
                  xs
                  ruby
                  :label="
                    t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.CONFIRM_DELETE')
                  "
                  :is-loading="deletingName === template.name"
                  @click.stop="deleteTemplate(template.name)"
                />
                <NextButton
                  xs
                  slate
                  faded
                  :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.CANCEL')"
                  @click.stop="confirmingDeleteName = ''"
                />
              </template>
              <NextButton
                v-else
                xs
                faded
                ruby
                icon="i-lucide-trash-2"
                @click.stop="confirmingDeleteName = template.name"
              />
            </div>
          </div>

          <div
            v-if="expandedTemplateName === template.name"
            class="px-4 pb-4 pt-1 border-t border-n-weak"
          >
            <div class="flex flex-col gap-3">
              <div
                v-if="getComponent(template, 'HEADER')"
                class="flex flex-col gap-1"
              >
                <span class="text-xs font-medium text-n-slate-10">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.LABEL') }}
                </span>
                <span
                  v-if="getComponent(template, 'HEADER').format === 'TEXT'"
                  class="text-sm text-n-slate-12"
                >
                  {{ getComponent(template, 'HEADER').text }}
                </span>
                <span v-else class="text-sm italic text-n-slate-11">
                  [{{ getComponent(template, 'HEADER').format }}]
                </span>
              </div>

              <div
                v-if="getComponent(template, 'BODY')"
                class="flex flex-col gap-1"
              >
                <span class="text-xs font-medium text-n-slate-10">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY.LABEL') }}
                </span>
                <p class="text-sm whitespace-pre-wrap text-n-slate-12">
                  {{ getComponent(template, 'BODY').text }}
                </p>
              </div>

              <div
                v-if="getComponent(template, 'FOOTER')"
                class="flex flex-col gap-1"
              >
                <span class="text-xs font-medium text-n-slate-10">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER.LABEL') }}
                </span>
                <span class="text-sm text-n-slate-11">
                  {{ getComponent(template, 'FOOTER').text }}
                </span>
              </div>

              <div
                v-if="getComponent(template, 'BUTTONS')"
                class="flex flex-col gap-1"
              >
                <span class="text-xs font-medium text-n-slate-10">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.LABEL') }}
                </span>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="(btn, idx) in getComponent(template, 'BUTTONS')
                      .buttons"
                    :key="idx"
                    class="px-2 py-1 text-xs rounded-md bg-n-slate-3 text-n-slate-12"
                  >
                    {{ btn.text }}
                  </span>
                </div>
              </div>

              <p
                v-if="
                  !getComponent(template, 'HEADER') &&
                  !getComponent(template, 'BODY') &&
                  !getComponent(template, 'FOOTER') &&
                  !getComponent(template, 'BUTTONS')
                "
                class="text-sm italic text-n-slate-10"
              >
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.NO_COMPONENTS') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Template Library -->
    <section class="flex flex-col gap-4">
      <div class="flex justify-between items-center">
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            Biblioteca de modelos
          </h3>
          <p class="text-sm text-n-slate-11">
            Selecione um modelo pré-aprovado do Meta para usar como base.
          </p>
        </div>
        <NextButton
          sm
          slate
          faded
          icon="i-lucide-library"
          :label="showLibrary ? 'Fechar biblioteca' : 'Abrir biblioteca'"
          @click="showLibrary ? (showLibrary = false) : openLibrary()"
        />
      </div>

      <div
        v-if="showLibrary"
        class="flex flex-col gap-4 p-4 rounded-lg border border-n-weak bg-n-slate-1"
      >
        <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div class="min-w-0 sm:w-40">
            <SelectInput
              v-model="libraryCategory"
              label="Categoria"
              :options="libraryCategoryOptions"
              @update:model-value="fetchLibrary"
            />
          </div>
          <div class="min-w-0 sm:w-44">
            <SelectInput
              v-model="libraryLanguage"
              label="Idioma"
              :options="libraryLanguageOptions"
              @update:model-value="fetchLibrary"
            />
          </div>
          <div class="flex-1 min-w-0">
            <Input
              v-model="librarySearch"
              placeholder="Pesquisar modelos..."
            />
          </div>
        </div>

        <div
          v-if="isLoadingLibrary"
          class="flex justify-center items-center py-8"
        >
          <Icon
            icon="i-lucide-loader-2"
            class="animate-spin size-5 text-n-slate-10"
          />
          <span class="ml-2 text-sm text-n-slate-11">
            Carregando modelos...
          </span>
        </div>

        <div
          v-else-if="libraryError"
          class="flex flex-col gap-2 items-center py-6"
        >
          <Icon
            icon="i-lucide-circle-alert"
            class="size-5 text-n-ruby-10"
          />
          <p class="text-sm text-center text-n-ruby-10">
            {{ libraryError }}
          </p>
          <NextButton
            xs
            slate
            faded
            label="Tentar novamente"
            @click="fetchLibrary"
          />
        </div>

        <p
          v-else-if="!filteredLibraryTemplates.length"
          class="py-6 text-sm text-center text-n-slate-11"
        >
          Nenhum modelo encontrado para esta categoria.
        </p>

        <div
          v-else
          class="flex flex-col gap-2 max-h-96 overflow-y-auto"
        >
          <div
            v-for="tpl in filteredLibraryTemplates"
            :key="tpl.id || tpl.name"
            class="rounded-lg outline outline-1 outline-n-weak bg-n-alpha-white"
          >
            <div
              class="flex gap-3 justify-between items-center p-3 cursor-pointer hover:bg-n-slate-2"
              @click="
                expandedLibraryName =
                  expandedLibraryName === tpl.name ? '' : tpl.name
              "
            >
              <div class="flex gap-2 items-center min-w-0">
                <Icon
                  :icon="
                    expandedLibraryName === tpl.name
                      ? 'i-lucide-chevron-down'
                      : 'i-lucide-chevron-right'
                  "
                  class="flex-shrink-0 size-4 text-n-slate-10"
                />
                <div class="flex flex-col gap-1 min-w-0">
                  <span class="text-sm font-medium truncate text-n-slate-12">
                    {{ tpl.name }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ tpl.category }} · {{ tpl.language }}
                  </span>
                </div>
              </div>
              <NextButton
                xs
                slate
                faded
                label="Usar"
                icon="i-lucide-plus"
                @click.stop="useLibraryTemplate(tpl)"
              />
            </div>

            <div
              v-if="expandedLibraryName === tpl.name"
              class="px-4 pb-4 pt-1 border-t border-n-weak"
            >
              <div class="flex flex-col gap-3">
                <div
                  v-if="getLibraryComponent(tpl, 'HEADER')"
                  class="flex flex-col gap-1"
                >
                  <span class="text-xs font-medium text-n-slate-10">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.LABEL') }}
                  </span>
                  <span class="text-sm text-n-slate-12">
                    {{
                      getLibraryComponent(tpl, 'HEADER').text ||
                      `[${getLibraryComponent(tpl, 'HEADER').format}]`
                    }}
                  </span>
                </div>

                <div
                  v-if="getLibraryComponent(tpl, 'BODY')"
                  class="flex flex-col gap-1"
                >
                  <span class="text-xs font-medium text-n-slate-10">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY.LABEL') }}
                  </span>
                  <p class="text-sm whitespace-pre-wrap text-n-slate-12">
                    {{ getLibraryComponent(tpl, 'BODY').text }}
                  </p>
                </div>

                <div
                  v-if="getLibraryComponent(tpl, 'FOOTER')"
                  class="flex flex-col gap-1"
                >
                  <span class="text-xs font-medium text-n-slate-10">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER.LABEL') }}
                  </span>
                  <span class="text-sm text-n-slate-11">
                    {{ getLibraryComponent(tpl, 'FOOTER').text }}
                  </span>
                </div>

                <div
                  v-if="getLibraryComponent(tpl, 'BUTTONS')"
                  class="flex flex-col gap-1"
                >
                  <span class="text-xs font-medium text-n-slate-10">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.LABEL') }}
                  </span>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="(btn, idx) in getLibraryComponent(tpl, 'BUTTONS')
                        .buttons"
                      :key="idx"
                      class="px-2 py-1 text-xs rounded-md bg-n-slate-3 text-n-slate-12"
                    >
                      {{ btn.text }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p
            v-if="filteredLibraryTemplates.length"
            class="pt-2 text-xs text-center text-n-slate-10"
          >
            {{ filteredLibraryTemplates.length }} modelo(s) encontrado(s)
          </p>
        </div>
      </div>
    </section>

    <!-- Create template -->
    <section
      class="flex flex-col gap-6 justify-between lg:flex-row lg:gap-10"
    >
      <div class="flex flex-col flex-1 gap-5 min-w-0">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TITLE') }}
        </h3>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectInput
            v-model="form.category"
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.LABEL')"
            :options="categoryOptions"
          />
          <SelectInput
            v-model="form.language"
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE')"
            :options="WHATSAPP_LANGUAGES"
          />
        </div>

        <Input
          v-model="form.name"
          :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME')"
          :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_PLACEHOLDER')"
          :message="nameError || ''"
          :message-type="nameError ? 'error' : 'info'"
        />

        <!-- Header -->
        <div class="flex flex-col gap-2">
          <SelectInput
            :model-value="form.headerFormat"
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.LABEL')"
            :options="headerFormatOptions"
            @update:model-value="onHeaderFormatChange"
          />
          <Input
            v-if="form.headerFormat === 'TEXT'"
            v-model="form.headerText"
            :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.TEXT_PLACEHOLDER')"
            maxlength="60"
          />
          <Input
            v-if="form.headerFormat === 'TEXT' && headerHasVariable"
            v-model="form.headerExample"
            :placeholder="
              t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.EXAMPLE_PLACEHOLDER')
            "
          />
          <div v-if="isMediaHeader" class="flex flex-col gap-1">
            <input
              type="file"
              :accept="mediaAccept"
              class="text-sm text-n-slate-11 file:mr-3 file:rounded-md file:border-0 file:bg-n-slate-3 file:px-3 file:py-1.5 file:text-n-slate-12"
              @change="onMediaChange"
            />
            <p class="text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.HEADER.MEDIA_HELP') }}
            </p>
          </div>
        </div>

        <!-- Body -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY.LABEL') }}
          </label>
          <textarea
            v-model="form.bodyText"
            rows="5"
            maxlength="1024"
            :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY.PLACEHOLDER')"
            class="p-3 w-full text-sm rounded-lg border resize-y border-n-weak bg-n-alpha-black1 text-n-slate-12"
          />
          <p class="text-xs text-n-slate-10">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY.HELP') }}
          </p>
          <div
            v-if="bodyVariables.length"
            class="flex flex-col gap-3 p-3 rounded-lg bg-n-slate-2"
          >
            <div>
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY.EXAMPLES_LABEL') }}
              </p>
              <p class="mt-0.5 text-xs text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY.EXAMPLES_HELP') }}
              </p>
            </div>
            <div
              v-for="variable in bodyVariables"
              :key="variable"
              class="flex flex-col gap-1"
            >
              <label class="text-xs font-medium text-n-slate-11">
                {{ variable }}
              </label>
              <Input
                v-model="bodyExamples[variable]"
                :placeholder="
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.BODY.EXAMPLE_INPUT', {
                    variable: variable,
                  })
                "
              />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <Input
          v-model="form.footerText"
          :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER.LABEL')"
          :placeholder="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FOOTER.PLACEHOLDER')"
          maxlength="60"
        />

        <!-- Buttons -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.LABEL') }}
          </label>
          <p class="text-xs text-n-slate-10">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.BUTTONS.HELP') }}
          </p>
          <TemplateButtonsBuilder v-model="form.buttons" />
        </div>

        <div class="flex flex-col gap-2 items-end pt-2">
          <p v-if="validationError" class="text-xs text-n-ruby-10">
            {{ validationError }}
          </p>
          <NextButton
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT')"
            :is-loading="isCreating"
            :disabled="!!validationError"
            @click="createTemplate"
          />
        </div>
      </div>

      <div class="flex-shrink-0 w-full lg:max-w-xs">
        <TemplatePreview
          :header="previewHeader"
          :body-text="form.bodyText"
          :footer-text="form.footerText"
          :buttons="form.buttons"
          :media-preview-url="mediaPreviewUrl"
        />
      </div>
    </section>
  </div>
</template>
